// /admin/empresas/:empresaId/crear-suscripciones?presupuesto_id=X
// Wizard para crear suscripcion(es) B2B desde un presupuesto aceptado.
//
// Crea entradas en `suscripciones_empresa` (NO en `clientes` — eso es B2C).
// Los items van como jsonb tomados del presupuesto, con precios ya negociados.
// Si la empresa tiene multi-direcciones: una suscripción por dirección con sus items asignados.
// Si no hay direcciones múltiples: una sola suscripción con todos los items.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const VENTANAS = [
  { v: 'manana', l: 'Mañana (10–14hs)' },
  { v: 'tarde', l: 'Tarde (14–18hs)' },
  { v: 'noche', l: 'Tarde-noche (18–21hs)' },
]

const DIAS = [
  { v: 'lunes', l: 'Lunes' },
  { v: 'martes', l: 'Martes' },
  { v: 'miercoles', l: 'Miércoles' },
  { v: 'jueves', l: 'Jueves' },
  { v: 'viernes', l: 'Viernes' },
  { v: 'sabado', l: 'Sábado' },
]

// Métodos de cobro B2B. transferencia es el default operativo hoy (sin proveedor).
// Los otros métodos usan MercadoPago (procesador único desde 2-jul-2026).
const METODOS_COBRO = [
  { v: 'transferencia',     l: '🏦 Transferencia bancaria', activo: true,  hint: 'Roberto chequea extracto y marca cobrado. Sin comisión.' },
  { v: 'manual',            l: '💵 Manual (efectivo/cheque/a coordinar)', activo: true, hint: 'Roberto cobra fuera del sistema y marca pagado.' },
  { v: 'cuenta_corriente',  l: '📋 Cuenta corriente (paga 30 días)', activo: true, hint: 'Entregamos, mandamos factura, cliente paga a 30 días.' },
  { v: 'link_pago',         l: '🔗 Link de pago online (MercadoPago)', activo: false, hint: 'Próximamente — checkout MercadoPago.' },
  { v: 'boleto',            l: '🎟 Boleto Abitab/RedPagos', activo: false, hint: 'Próximamente — vía MercadoPago.' },
  { v: 'tarjeta_auto',      l: '💳 Tarjeta automática (MP suscripción · cobro cada 30 días)', activo: false, hint: 'Próximamente — tokenización con MercadoPago.' },
]

export default function CrearSuscripcionesB2B() {
  const { empresaId } = useParams()
  const [searchParams] = useSearchParams()
  const presupuestoId = searchParams.get('presupuesto_id')
  const nav = useNavigate()

  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [creando, setCreando] = useState(false)
  const [exito, setExito] = useState(null)

  const [empresa, setEmpresa] = useState(null)
  const [presupuesto, setPresupuesto] = useState(null)
  const [direcciones, setDirecciones] = useState([])

  // Método de cobro común a todas las suscripciones del presupuesto
  const [metodoCobro, setMetodoCobro] = useState('transferencia')

  // Plan: lista de suscripciones a crear, una por dirección (o una sola si no hay direcciones)
  // { direccion_id, alias, items[], precio_mensual, dia_entrega, ventana_horaria, incluir }
  const [plan, setPlan] = useState([])

  useEffect(() => {
    (async () => {
      try {
        setCargando(true)
        const [{ data: e, error: eE }, { data: p, error: eP }, { data: dirs, error: eD }] = await Promise.all([
          supabase.from('empresas').select('id, razon_social, rut, tipo, facturacion_modo').eq('id', empresaId).single(),
          presupuestoId
            ? supabase.from('presupuestos').select('*, items:presupuestos_items(*, producto:productos(nombre, marca, presentacion))').eq('id', presupuestoId).single()
            : Promise.resolve({ data: null, error: null }),
          supabase.from('empresas_direcciones').select('id, alias, calle, numero, apto_unidad, barrio_id, barrio:barrios(id, nombre, dias)').eq('empresa_id', empresaId).eq('activa', true).order('alias'),
        ])

        if (eE) throw new Error('Empresa: ' + eE.message)
        if (eP) throw new Error('Presupuesto: ' + eP.message)
        if (eD) throw new Error('Direcciones: ' + eD.message)

        setEmpresa(e)
        setPresupuesto(p)
        setDirecciones(dirs || [])

        // Armar plan inicial
        if (!p) {
          // Sin presupuesto: una suscripción vacía por cada dirección activa (Gimena llena items a mano)
          setPlan((dirs || []).map(d => ({
            direccion_id: d.id,
            alias: d.alias,
            barrio_nombre: d.barrio?.nombre || '',
            dias_barrio: d.barrio?.dias || [],
            items: [],
            precio_mensual: 0,
            dia_entrega: (d.barrio?.dias && d.barrio.dias[0]) || 'lunes',
            ventana_horaria: 'manana',
            incluir: true,
          })))
        } else {
          // Con presupuesto: agrupar items por dirección
          const itemsDelPresupuesto = (p.items || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
          const dirsList = dirs || []

          if (dirsList.length === 0) {
            // Sin direcciones: una sola suscripción con todos los items
            const total = sumarItems(itemsDelPresupuesto)
            setPlan([{
              direccion_id: null,
              alias: empresa?.razon_social || 'Suscripción única',
              barrio_nombre: '',
              dias_barrio: [],
              items: itemsDelPresupuesto.map(mapItemPresupuesto),
              precio_mensual: total,
              dia_entrega: 'lunes',
              ventana_horaria: 'manana',
              incluir: true,
            }])
          } else {
            // Con direcciones: para cada dirección, los items que tienen direccion_id = esa
            // Items sin direccion_id van replicados a todas las direcciones (servicio común)
            const itemsConDir = itemsDelPresupuesto.filter(it => it.direccion_id)
            const itemsSinDir = itemsDelPresupuesto.filter(it => !it.direccion_id)

            setPlan(dirsList.map(d => {
              const itemsAqui = [
                ...itemsConDir.filter(it => it.direccion_id === d.id),
                ...itemsSinDir, // los items sin dirección van a todas
              ]
              const total = sumarItems(itemsAqui)
              return {
                direccion_id: d.id,
                alias: d.alias,
                barrio_nombre: d.barrio?.nombre || '',
                dias_barrio: d.barrio?.dias || [],
                items: itemsAqui.map(mapItemPresupuesto),
                precio_mensual: total,
                dia_entrega: (d.barrio?.dias && d.barrio.dias[0]) || 'lunes',
                ventana_horaria: 'manana',
                incluir: true,
              }
            }))
          }
        }
      } catch (err) {
        setError(err.message)
      } finally {
        setCargando(false)
      }
    })()
  }, [empresaId, presupuestoId])

  function updatePlan(idx, patch) {
    setPlan(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s))
  }

  function actualizarPrecio(idx, nuevoPrecio) {
    updatePlan(idx, { precio_mensual: Number(nuevoPrecio) || 0 })
  }

  const planIncluido = useMemo(() => plan.filter(s => s.incluir), [plan])
  const totalMensual = useMemo(() => planIncluido.reduce((sum, s) => sum + (s.precio_mensual || 0), 0), [planIncluido])

  async function crearTodas() {
    setError(null)
    if (planIncluido.length === 0) {
      setError('Marcá al menos una suscripción para crear')
      return
    }
    for (const s of planIncluido) {
      if (!s.items || s.items.length === 0) {
        setError(`"${s.alias}" no tiene items. Cancela o agregá items desde el presupuesto.`)
        return
      }
      if (!s.precio_mensual || s.precio_mensual <= 0) {
        setError(`"${s.alias}" tiene precio mensual 0 o negativo.`)
        return
      }
    }
    setCreando(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const owner = user?.id || null

      const payload = planIncluido.map(s => {
        const subtotalItems = (s.items || []).reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0)
        const precio = Number(s.precio_mensual) || 0
        // Si el precio es menor que la suma de items, calculamos el descuento global declarativo
        const descuentoPct = subtotalItems > 0 && subtotalItems > precio
          ? round2(((subtotalItems - precio) / subtotalItems) * 100)
          : 0
        return {
          empresa_id: empresaId,
          direccion_id: s.direccion_id,
          presupuesto_origen_id: presupuestoId || null,
          alias: s.alias,
          items: s.items,
          precio_mensual_total: round2(precio),
          descuento_pct: descuentoPct,
          moneda: 'UYU',
          dia_entrega: s.dia_entrega,
          ventana_horaria: s.ventana_horaria,
          estado: 'activa',
          modelo_cobro: metodoCobro,
          facturacion_modo: empresa.facturacion_modo,
          owner_id: owner,
          created_by: owner,
          notas: presupuesto ? `Generada desde presupuesto Nº ${presupuesto.numero}` : null,
        }
      })

      const { data: creadas, error: eIns } = await supabase
        .from('suscripciones_empresa')
        .insert(payload)
        .select('id, alias')
      if (eIns) throw eIns

      // Marcar presupuesto como aceptado si todavía no
      if (presupuesto && presupuesto.estado !== 'aceptado') {
        await supabase.from('presupuestos').update({
          estado: 'aceptado',
          aceptado_at: presupuesto.aceptado_at || new Date().toISOString(),
        }).eq('id', presupuesto.id)
      }

      // Cambiar pipeline de la empresa a "activo"
      if (empresa.estado_pipeline !== 'activo') {
        await supabase.from('empresas').update({ estado_pipeline: 'activo' }).eq('id', empresaId)
      }

      // Registrar interacción
      await supabase.from('empresas_interacciones').insert({
        empresa_id: empresaId,
        tipo: 'nota',
        autor_id: owner,
        resumen: `Se activaron ${creadas.length} suscripción(es) B2B`,
        notas: `Desde presupuesto Nº ${presupuesto?.numero || '(sin presupuesto)'} · Total mensual: $${totalMensual.toLocaleString('es-UY')}`,
        presupuesto_id: presupuestoId || null,
      })

      setExito({ cantidad: creadas.length })
    } catch (err) {
      setError(err.message || 'Error al crear suscripciones')
    } finally {
      setCreando(false)
    }
  }

  if (cargando) {
    return <AdminLayout title="Crear suscripción B2B"><div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>Cargando…</div></AdminLayout>
  }

  if (!empresa) {
    return <AdminLayout title="Crear suscripción B2B"><div style={{ padding: 24, color: '#991B1B' }}>{error || 'Empresa no encontrada'}</div></AdminLayout>
  }

  if (exito) {
    return (
      <AdminLayout title="Suscripciones activadas">
        <Estilos />
        <div className="cs-exito">
          <div className="cs-exito-icon">✓</div>
          <h2>Activadas {exito.cantidad} suscripción{exito.cantidad > 1 ? 'es' : ''}</h2>
          <p>Las podés ver en el tab <strong>Servicio</strong> de la empresa.</p>
          <p style={{ fontSize: 13, color: '#64748B', marginTop: 14, lineHeight: 1.6 }}>
            <strong>Próximos pasos:</strong><br />
            🟡 <strong>Cobro:</strong> MP cobra automático cada 30 días desde la primera autorización. Hoy se cobra manual (transferencia o MP) hasta que se termine el flow de tokenización B2B.<br />
            🟡 <strong>Facturación:</strong> Zsoftware emite la e-Factura cuando esté integrado + RUT habilitado. Hoy Roberto emite manual.
          </p>
          <div className="cs-exito-actions">
            <button className="cs-btn-primary" onClick={() => nav(`/admin/empresas/${empresaId}`)}>Ir a {empresa.razon_social}</button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="Crear suscripción B2B">
      <Estilos />
      <div className="cs-page">
        <button className="cs-back" onClick={() => nav(`/admin/empresas/${empresaId}`)}>← Volver a {empresa.razon_social}</button>

        <header className="cs-head">
          <h1>Activar suscripción(es) B2B</h1>
          <p>
            Empresa: <strong>{empresa.razon_social}</strong>
            {presupuesto && <> · desde presupuesto Nº <strong>{presupuesto.numero}</strong></>}
            {direcciones.length > 0 && <> · <strong>{direcciones.length}</strong> direcciones activas</>}
          </p>
        </header>

        <section className="cs-card">
          <h3>💳 Método de cobro</h3>
          <p className="cs-help">
            Aplica a todas las suscripciones que actives ahora. Después podés cambiarlo por suscripción si hace falta.
          </p>
          <div className="cs-metodos">
            {METODOS_COBRO.map(m => (
              <label key={m.v} className={`cs-metodo${metodoCobro === m.v ? ' on' : ''}${!m.activo ? ' off' : ''}`}>
                <input
                  type="radio"
                  name="metodoCobro"
                  value={m.v}
                  checked={metodoCobro === m.v}
                  disabled={!m.activo}
                  onChange={() => setMetodoCobro(m.v)}
                />
                <div>
                  <div className="cs-metodo-label">{m.l} {!m.activo && <span className="cs-metodo-pill">próximamente</span>}</div>
                  <div className="cs-metodo-hint">{m.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        <section className="cs-card">
          <h3>📦 Suscripciones a activar ({planIncluido.length})</h3>
          {plan.length === 0 && (
            <div className="cs-warn">
              ⚠ La empresa no tiene direcciones cargadas. Cargá al menos una en el detalle de la empresa antes de activar suscripciones.
            </div>
          )}
          {plan.map((s, idx) => (
            <div key={idx} className={`cs-sub${s.incluir ? '' : ' off'}`}>
              <header className="cs-sub-head">
                <label className="cs-check">
                  <input type="checkbox" checked={s.incluir} onChange={e => updatePlan(idx, { incluir: e.target.checked })} />
                  <strong>{s.alias}</strong>
                </label>
                {s.barrio_nombre && <span className="cs-sub-barrio">{s.barrio_nombre}</span>}
              </header>

              {s.incluir && (
                <div className="cs-sub-body">
                  {/* Items de la suscripción */}
                  <div className="cs-items">
                    <div className="cs-items-label">Items mensuales ({s.items.length})</div>
                    {s.items.length === 0 ? (
                      <div className="cs-items-empty">Sin items asignados — el presupuesto no tiene items para esta dirección.</div>
                    ) : (
                      <ul className="cs-items-list">
                        {s.items.map((it, i) => (
                          <li key={i}>
                            <span className="cs-it-desc">{it.descripcion}</span>
                            <span className="cs-it-cant">×{it.cantidad}</span>
                            <span className="cs-it-precio">${Number(it.subtotal).toLocaleString('es-UY')}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="cs-grid-3">
                    <Field label="Precio mensual total (UYU c/IVA)">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={s.precio_mensual}
                        onChange={e => actualizarPrecio(idx, e.target.value)}
                      />
                    </Field>
                    <Field label="Día de entrega">
                      <select value={s.dia_entrega} onChange={e => updatePlan(idx, { dia_entrega: e.target.value })}>
                        {DIAS.map(d => (
                          <option key={d.v} value={d.v} disabled={s.dias_barrio?.length && !s.dias_barrio.includes(d.v)}>
                            {d.l}{s.dias_barrio?.length && !s.dias_barrio.includes(d.v) ? ' (fuera ruta)' : ''}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Ventana horaria">
                      <select value={s.ventana_horaria} onChange={e => updatePlan(idx, { ventana_horaria: e.target.value })}>
                        {VENTANAS.map(v => <option key={v.v} value={v.v}>{v.l}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>
              )}
            </div>
          ))}
        </section>

        {planIncluido.length > 0 && (
          <section className="cs-card cs-summary">
            <div>
              <strong>{planIncluido.length}</strong> suscripción(es) · total mensual:
              <span className="cs-total"> ${totalMensual.toLocaleString('es-UY')}/mes</span>
            </div>
          </section>
        )}

        {error && <div className="cs-error">⚠ {error}</div>}

        <div className="cs-foot">
          <button className="cs-btn-secondary" onClick={() => nav(-1)} disabled={creando}>Cancelar</button>
          <button className="cs-btn-primary" onClick={crearTodas} disabled={creando || planIncluido.length === 0}>
            {creando ? 'Activando…' : `✓ Activar ${planIncluido.length} suscripción${planIncluido.length === 1 ? '' : 'es'}`}
          </button>
        </div>

        <div className="cs-info">
          <h4>📌 Qué pasa después</h4>
          <ol>
            <li>Las suscripciones quedan en estado <code>activa</code> linkeadas a la empresa, dirección y presupuesto origen.</li>
            <li>Aparecen en el tab <strong>Servicio</strong> de la empresa.</li>
            <li>El cobro y facturación todavía son manuales. Cuando lleguen Plexo + Zsoftware se automatiza todo sin tocar nada más.</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  )
}

// Mapea un item de presupuesto al formato de items jsonb de suscripciones_empresa
function mapItemPresupuesto(it) {
  let descripcion = ''
  if (it.tipo === 'producto' && it.producto) {
    descripcion = [it.producto.marca, it.producto.nombre, it.producto.presentacion].filter(Boolean).join(' · ')
  } else if (it.tipo === 'kit' && it.kit_codigo) {
    descripcion = `Kit ${it.kit_codigo}`
  } else {
    descripcion = it.descripcion || ''
  }
  return {
    tipo: it.tipo,
    producto_id: it.producto_id || null,
    kit_codigo: it.kit_codigo || null,
    descripcion,
    cantidad: Number(it.cantidad) || 0,
    precio_unitario: Number(it.precio_unitario) || 0,
    descuento_pct: Number(it.descuento_pct || 0),
    iva_pct: Number(it.iva_pct) || 22,
    subtotal: Number(it.subtotal) || (Number(it.cantidad) * Number(it.precio_unitario) * (1 - Number(it.descuento_pct || 0) / 100)),
  }
}

function sumarItems(items) {
  return items.reduce((sum, it) => {
    const sub = Number(it.subtotal) || (Number(it.cantidad) * Number(it.precio_unitario) * (1 - Number(it.descuento_pct || 0) / 100))
    return sum + sub
  }, 0)
}

function round2(n) { return Math.round(n * 100) / 100 }

function Field({ label, children }) {
  return (
    <label className="cs-field">
      <span>{label}</span>
      {children}
    </label>
  )
}

function Estilos() {
  return (
    <style>{`
      .cs-page { max-width: 920px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .cs-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .cs-back:hover { color: #1A3461; }
      .cs-head { margin-bottom: 18px; }
      .cs-head h1 { margin: 0 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .cs-head p { margin: 0; font-size: 13.5px; color: #64748B; }

      .cs-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 18px 20px; margin-bottom: 14px; }
      .cs-card h3 { margin: 0 0 12px; font-size: 14px; font-weight: 800; color: #1A3461; }

      .cs-sub { border: 1px solid #E2E8F0; border-radius: 10px; padding: 12px 14px; margin-bottom: 12px; background: #F8FAFC; }
      .cs-sub.off { opacity: 0.55; }
      .cs-sub-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
      .cs-check { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 14px; }
      .cs-check input { width: 16px; height: 16px; cursor: pointer; }
      .cs-sub-barrio { background: #DBEAFE; color: #1E40AF; font-size: 11px; padding: 3px 9px; border-radius: 999px; font-weight: 700; }
      .cs-sub-body { display: grid; gap: 12px; }

      .cs-items { background: #fff; border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px 12px; }
      .cs-items-label { font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
      .cs-items-empty { font-size: 12px; color: #64748B; font-style: italic; }
      .cs-items-list { list-style: none; padding: 0; margin: 0; font-size: 13px; }
      .cs-items-list li { display: grid; grid-template-columns: 1fr auto auto; gap: 8px; padding: 4px 0; border-bottom: 1px dashed #F1F5F9; }
      .cs-items-list li:last-child { border-bottom: none; }
      .cs-it-cant { color: #64748B; font-variant-numeric: tabular-nums; }
      .cs-it-precio { color: #1A3461; font-weight: 700; font-variant-numeric: tabular-nums; }

      .cs-field { display: block; }
      .cs-field > span { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      .cs-field input, .cs-field select { width: 100%; padding: 8px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0; font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box; }
      .cs-field input:focus, .cs-field select:focus { outline: none; border-color: #00A8B5; }
      .cs-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
      @media (max-width: 640px) { .cs-grid-3 { grid-template-columns: 1fr; } }

      .cs-metodos { display: grid; gap: 8px; }
      .cs-metodo {
        display: flex; gap: 12px; align-items: flex-start; padding: 10px 12px;
        border: 1.5px solid #E2E8F0; border-radius: 10px; background: #fff; cursor: pointer;
        transition: all 0.12s;
      }
      .cs-metodo:hover:not(.off) { border-color: #00A8B5; background: #F0FDFA; }
      .cs-metodo.on { border-color: #00A8B5; background: #F0FDFA; box-shadow: 0 0 0 3px rgba(0,168,181,0.12); }
      .cs-metodo.off { opacity: 0.5; cursor: not-allowed; }
      .cs-metodo input[type="radio"] { width: 16px; height: 16px; margin-top: 2px; cursor: inherit; accent-color: #00A8B5; }
      .cs-metodo-label { font-size: 13.5px; font-weight: 700; color: #1A3461; line-height: 1.3; }
      .cs-metodo-hint { font-size: 11.5px; color: #64748B; margin-top: 2px; line-height: 1.4; }
      .cs-metodo-pill { background: #FEF3C7; color: #78350F; font-size: 9.5px; padding: 2px 7px; border-radius: 999px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-left: 6px; }

      .cs-summary { background: #1A3461; color: #fff; padding: 14px 18px; border: none; font-size: 14px; }
      .cs-summary .cs-total { font-weight: 800; color: #00D4E0; font-size: 15.5px; margin-left: 6px; }

      .cs-warn { background: #FEF3C7; border-left: 3px solid #F59E0B; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #78350F; font-size: 13px; margin-bottom: 12px; }
      .cs-error { background: #FEE2E2; border-left: 3px solid #DC2626; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #991B1B; font-size: 13px; margin-bottom: 12px; }

      .cs-foot { display: flex; justify-content: flex-end; gap: 8px; margin-bottom: 18px; }
      .cs-btn-primary, .cs-btn-secondary { padding: 10px 18px; border-radius: 8px; font-size: 13.5px; font-weight: 700; font-family: inherit; cursor: pointer; transition: all 0.15s; border: none; }
      .cs-btn-primary { background: #1A3461; color: #fff; }
      .cs-btn-primary:hover { background: #1A3461; }
      .cs-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
      .cs-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .cs-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }

      .cs-info { background: #F0FDFA; border-left: 3px solid #00A8B5; padding: 14px 18px; border-radius: 0 8px 8px 0; font-size: 12.5px; color: #134E4A; }
      .cs-info h4 { margin: 0 0 8px; font-size: 11.5px; font-weight: 800; color: #1A3461; text-transform: uppercase; letter-spacing: 0.06em; }
      .cs-info ol { margin: 0; padding-left: 20px; line-height: 1.6; }
      .cs-info code { background: rgba(0,168,181,0.12); padding: 1px 6px; border-radius: 4px; font-family: 'SF Mono', monospace; font-size: 11.5px; }

      .cs-exito { max-width: 560px; margin: 60px auto; text-align: center; padding: 32px 28px; background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; }
      .cs-exito-icon { font-size: 56px; color: #16A34A; line-height: 1; margin-bottom: 12px; }
      .cs-exito h2 { margin: 0 0 8px; font-size: 22px; font-weight: 900; }
      .cs-exito p { margin: 4px 0; font-size: 14px; color: #475569; }
      .cs-exito-actions { display: flex; gap: 10px; justify-content: center; margin-top: 22px; flex-wrap: wrap; }
    `}</style>
  )
}
